import { useState, useEffect, useCallback } from "react";
import { Button } from "./Button";
import { useAuth } from "../lib/auth.jsx";
import { Modal } from "./DesignDetailModal";
import { designImageUrl } from "../lib/designMedia.js";

function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);
  const handleResize = useCallback(() => setWidth(window.innerWidth), []);
  useEffect(() => {
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);
  return width;
}

export function DesignCard({ design: d, selected, onSelect, onFinalise, index = 0, requestId, designIndex }) {
  const { authedFetch, session } = useAuth();
  const [favBusy, setFavBusy] = useState(false);
  const [favored, setFavored] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;

  async function toggleFav(e) {
    e.stopPropagation();
    if (!session || !requestId) return;
    setFavBusy(true);
    try {
      if (!favored) {
        await authedFetch("/api/favorites", { method: "POST",
          body: JSON.stringify({ requestId, designIndex }) });
        setFavored(true);
      }
    } catch {/* ignore */}
    setFavBusy(false);
  }
  const [hover, setHover] = useState(false);
  const colors = d.colors || ["#E8B4B8", "#D4AF6A", "#C4879A"];
  const img = designImageUrl(d);

  return (
    <article
      className="cardIn"
      onClick={() => onSelect(d)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(d); } }}
      tabIndex={0}
      role="button"
      aria-pressed={selected}
      aria-label={`${d.name} — ${d.tagline}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: selected ? "linear-gradient(160deg, var(--plum), var(--noir))" : "var(--bg-elevated)",
        borderRadius: "var(--radius-lg)",
        overflow: "hidden", cursor: "pointer",
        border: `2px solid ${selected ? "var(--gold)" : "var(--border)"}`,
        boxShadow: selected ? "0 18px 48px rgba(13, 8, 16, 0.45)" : hover ? "0 12px 36px rgba(13, 8, 16, 0.18)" : "var(--shadow-sm)",
        transition: "transform .35s var(--ease-spring), box-shadow .3s var(--ease-out), border-color .25s",
        transform: hover && !selected ? "translateY(-4px)" : "translateY(0)",
        position: "relative",
        animationDelay: `${index * 0.07}s`,
      }}
    >
      {img && (
        <div style={{ position: "relative", aspectRatio: "1 / 1", overflow: "hidden", background: "var(--bg-soft)" }}>
          <img src={img} alt={`${d.name} preview`}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", inset: 0,
            background: "linear-gradient(180deg, transparent 60%, rgba(13,8,16,0.55))",
            pointerEvents: "none" }} />
          {session && requestId && (
            <button type="button" onClick={toggleFav} disabled={favBusy}
              aria-label={favored ? "Saved to favorites" : "Save to favorites"}
              style={{
                position: "absolute", top: 12, right: 12,
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(13,8,16,0.55)", border: "1.5px solid rgba(255,255,255,0.25)",
                color: favored ? "#FF6B9D" : "#fff", fontSize: 18, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                backdropFilter: "blur(8px)",
              }}>
              {favored ? "♥" : "♡"}
            </button>
          )}
          <div style={{ position: "absolute", left: 14, bottom: 12, right: 14,
            display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 10 }}>
            <span style={{ fontSize: 11, padding: "4px 10px", borderRadius: 999, background: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, backdropFilter: "blur(8px)" }}>
              {d.priceRange}
            </span>
          </div>
        </div>
      )}
      <div style={{ height: 6, display: "flex" }} aria-hidden="true">
        {colors.map((c, i) => (<div key={i} style={{ flex: 1, background: c }} />))}
      </div>
      <div style={{ padding: isMobile ? "12px 14px 14px" : "16px 18px 18px" }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, color: selected ? "#fff" : "var(--text)", lineHeight: 1.2 }}>
            {d.name}
          </div>
          <div style={{ fontSize: 12, fontStyle: "italic", marginTop: 3, color: selected ? "var(--gold-light)" : "var(--accent)", fontWeight: 500 }}>
            {d.tagline}
          </div>
        </div>
        <p style={{ fontSize: isMobile ? 13 : 12.5, color: selected ? "rgba(245, 230, 232, 0.85)" : "var(--text-soft)", lineHeight: 1.7, margin: "0 0 14px" }}>
          {d.description}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
          {[["Shape", d.nailShape], ["Length", d.nailLength], ["Finish", d.finish], ["Time", d.duration]].map(([l, val]) => (
            <div key={l} style={{
              background: selected ? "rgba(255, 255, 255, 0.06)" : "var(--bg-soft)",
              borderRadius: 10, padding: "6px 10px",
              border: selected ? "1px solid rgba(255, 255, 255, 0.08)" : "none",
            }}>
              <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 600,
                color: selected ? "rgba(201, 168, 76, 0.75)" : "var(--text-muted)" }}>{l}</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: selected ? "#fff" : "var(--text)", marginTop: 2 }}>
                {val || "—"}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 12 }}>
          <span style={{ fontSize: 11, background: selected ? "rgba(201, 168, 76, 0.18)" : "rgba(201, 168, 76, 0.12)",
            color: selected ? "var(--gold-light)" : "#6A4000", borderRadius: 999, padding: "4px 10px",
            border: `1px solid ${selected ? "rgba(201, 168, 76, 0.32)" : "rgba(201, 168, 76, 0.22)"}`, fontWeight: 600 }}>
            🎨 {d.technique}
          </span>
          <span style={{ fontSize: 11, background: selected ? "rgba(255, 255, 255, 0.07)" : "rgba(196, 135, 154, 0.12)",
            color: selected ? "rgba(255, 255, 255, 0.78)" : "#7A3A50", borderRadius: 999, padding: "4px 10px", fontWeight: 600 }}>
            ⏱ {d.difficulty}
          </span>
        </div>
        {selected && (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 0.85fr) minmax(0, 1.15fr)", gap: 8 }}>
            <Button label="View Details" variant="outline"
              onClick={(e) => { e.stopPropagation(); setShowDetails(true); }} />
            <Button label="Book now" icon="→" variant="gold"
              onClick={(e) => { e.stopPropagation(); onFinalise(d); }} />
          </div>
        )}
      </div>
      {selected && (
        <div aria-hidden="true" style={{
          position: "absolute", top: 12, right: 12, width: 26, height: 26, borderRadius: "50%",
          background: "linear-gradient(135deg, var(--gold), #8A5A00)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontSize: 13, fontWeight: 700, boxShadow: "0 4px 12px rgba(201, 168, 76, 0.5)",
        }}>✓</div>
      )}
      {showDetails && (
        <Modal title={d.name || "Design details"} onClose={() => setShowDetails(false)} maxWidth={720}>
          <GeneratedDesignDetails design={d} onBook={() => { setShowDetails(false); onFinalise(d); }} />
        </Modal>
      )}
    </article>
  );
}

function GeneratedDesignDetails({ design: d, onBook }) {
  const imageUrl = designImageUrl(d);
  return (
    <div style={{ display: "grid", gap: 14 }}>
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          style={{ width: "100%", maxHeight: 430, objectFit: "cover", borderRadius: 16, border: "1px solid var(--border)" }}
        />
      )}
      <div>
        <div style={{ color: "var(--text-accent)", fontSize: 13, fontWeight: 800 }}>{d.tagline}</div>
        <p style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.7, margin: "8px 0 0" }}>{d.description}</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        {[["Shape", d.nailShape], ["Length", d.nailLength], ["Finish", d.finish], ["Time", d.duration], ["Price", d.priceRange], ["Difficulty", d.difficulty]].map(([label, value]) => (
          <div key={label} style={{ background: "var(--bg-soft)", border: "1px solid var(--border)", borderRadius: 12, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase" }}>{label}</div>
            <div style={{ color: "var(--text)", fontWeight: 700, marginTop: 3 }}>{value || "—"}</div>
          </div>
        ))}
      </div>
      {Array.isArray(d.products) && d.products.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {d.products.map((p, i) => (
            <span key={i} style={{ fontSize: 12, borderRadius: 999, padding: "5px 10px", background: "rgba(196,135,154,0.14)", color: "var(--text-accent)", border: "1px solid var(--border-strong)", fontWeight: 700 }}>
              {p}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button label="Book now" icon="→" variant="gold" onClick={onBook} />
      </div>
    </div>
  );
}
